import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { needs2FA } from '../utils/twoFactor';

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      try {
        // getUser() (not just reading the local session) re-validates against
        // Supabase and returns fresh app_metadata - a cached session's JWT can
        // still show admin:true for up to an hour after a revoke, same
        // limitation Firebase's own cached ID tokens had. Called directly on
        // mount instead of relying on onAuthStateChange's implicit "fire once
        // immediately" behavior - under React 18 StrictMode this effect
        // mounts, cleans up (unsubscribing), then mounts again, and if that
        // first automatic event landed on the subscription that got torn
        // down, the surviving one could end up believing there's no session
        // at all right after a real, successful login+2FA.
        const { data, error } = await supabase.auth.getUser();
        if (cancelled) return;
        if (error || !data.user) {
          setIsAuthenticated(false);
          setIsLoading(false);
          navigate('/admin/login', { replace: true });
          return;
        }
        const user = data.user;

        // Check if user has admin claim
        if (user.app_metadata?.admin !== true) {
          setIsAuthenticated(false);
          setIsLoading(false);
          navigate('/admin/login', { replace: true });
          return;
        }

        // A valid admin claim only proves the password was correct - it doesn't
        // prove 2FA was completed. A session that's signed in but never finished
        // (or expired) 2FA must not reach admin pages just by navigating here directly.
        if (await needs2FA(user.email)) {
          if (cancelled) return;
          await supabase.auth.signOut();
          setIsAuthenticated(false);
          setIsLoading(false);
          navigate('/admin/login', { replace: true });
          return;
        }

        if (cancelled) return;
        setIsAuthenticated(true);
        setIsLoading(false);
      } catch (error) {
        if (cancelled) return;
        console.error('❌ Error checking admin privileges:', error);
        setIsAuthenticated(false);
        setIsLoading(false);
        navigate('/admin/login', { replace: true });
      }
    };

    checkAuth();

    // Ongoing protection while already on a protected page - only reacts to a
    // real sign-out, not the initial/synthetic events already handled above.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        navigate('/admin/login', { replace: true });
      }
    });

    return () => { cancelled = true; subscription.unsubscribe(); };
  }, [navigate]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        fontFamily: 'Poppins, sans-serif',
        backgroundColor: '#f5f7fa'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e2e8f0',
          borderTop: '4px solid #010f3c',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '15px'
        }}></div>
        <p style={{ color: '#010f3c', fontSize: '16px', margin: 0 }}>
          Verifying authentication...
        </p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Only render children if authenticated
  return isAuthenticated ? children : null;
};

export default ProtectedRoute;
